import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import AttachmentIcon from '@mui/icons-material/Attachment';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Attachment,
  downloadDbAttachment,
  downloadLiveAttachment,
  importGpgKey,
  readAttachmentText,
} from '../api/mailbox';

type LiveRef = { account: number; folder: string; uid: number };

interface MessageAttachmentsProps {
  attachments: Attachment[];
  /** Live IMAP message identity; omit for DB-backed messages. */
  live?: LiveRef;
  /** DB message id when not live. */
  messageId?: number;
}

function isViewableInline(contentType: string): boolean {
  const ct = (contentType || '').toLowerCase();
  return ct.startsWith('image/') || ct === 'application/pdf' || ct.startsWith('text/');
}

export default function MessageAttachments({
  attachments,
  live,
  messageId,
}: MessageAttachmentsProps) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [pgpPrompt, setPgpPrompt] = useState<Attachment | null>(null);
  const [pgpDismissed, setPgpDismissed] = useState<Set<string>>(() => new Set());
  const [pgpImported, setPgpImported] = useState<Set<string>>(() => new Set());
  const [busyId, setBusyId] = useState<string | number | null>(null);

  const attKey = (a: Attachment) => `${a.id}-${a.filename}`;

  const isKeyKnown = (a: Attachment) =>
    Boolean(a.pgp_key_already_imported) || pgpImported.has(attKey(a));

  const pgpCandidates = useMemo(
    () =>
      attachments.filter(
        (a) =>
          a.is_pgp_public_key &&
          !a.pgp_key_already_imported &&
          !pgpImported.has(`${a.id}-${a.filename}`)
      ),
    [attachments, pgpImported]
  );

  useEffect(() => {
    if (pgpPrompt) return;
    const next = pgpCandidates.find((a) => !pgpDismissed.has(attKey(a)));
    if (next) setPgpPrompt(next);
  }, [pgpCandidates, pgpDismissed, pgpPrompt]);

  const importMutation = useMutation({
    mutationFn: async (att: Attachment) => {
      const text = await readAttachmentText({
        live,
        messageId,
        attachment: att,
      });
      return importGpgKey(text);
    },
    onSuccess: (_data, att) => {
      qc.invalidateQueries({ queryKey: ['gpg-keys'] });
      const key = attKey(att);
      setPgpImported((prev) => new Set(prev).add(key));
      setPgpDismissed((prev) => new Set(prev).add(key));
      setPgpPrompt(null);
      setError(null);
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to import PGP key.';
      setError(String(detail));
    },
  });

  const runDownload = async (att: Attachment, disposition: 'attachment' | 'inline') => {
    setBusyId(att.id);
    setError(null);
    try {
      const blob = live
        ? await downloadLiveAttachment({
            ...live,
            index: att.index ?? att.id,
            disposition,
          })
        : await downloadDbAttachment({
            messageId: messageId!,
            attachmentId: att.id,
            disposition,
          });
      const url = URL.createObjectURL(blob);
      if (disposition === 'inline') {
        window.open(url, '_blank', 'noopener,noreferrer');
        // Revoke later so the new tab can load
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = att.filename || 'attachment';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to download attachment.';
      setError(String(detail));
    } finally {
      setBusyId(null);
    }
  };

  if (!attachments.length) return null;

  return (
    <>
      <Typography variant="subtitle2" gutterBottom>
        Attachments ({attachments.length})
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {attachments.map((att) => (
          <Box key={att.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              icon={<AttachmentIcon />}
              label={`${att.filename}${att.size ? ` (${Math.max(1, Math.round(att.size / 1024))} KB)` : ''}`}
              size="small"
              disabled={busyId === att.id}
              onClick={() =>
                runDownload(att, isViewableInline(att.content_type) ? 'inline' : 'attachment')
              }
            />
            <Chip
              icon={<DownloadIcon />}
              label="Download"
              size="small"
              variant="outlined"
              disabled={busyId === att.id}
              onClick={() => runDownload(att, 'attachment')}
            />
            {isViewableInline(att.content_type) && (
              <Chip
                icon={<VisibilityIcon />}
                label="View"
                size="small"
                variant="outlined"
                disabled={busyId === att.id}
                onClick={() => runDownload(att, 'inline')}
              />
            )}
            {att.is_pgp_public_key &&
              (isKeyKnown(att) ? (
                <Chip
                  icon={<VpnKeyIcon />}
                  label="Key on file"
                  size="small"
                  variant="outlined"
                  color="success"
                />
              ) : (
                <Chip
                  icon={<VpnKeyIcon />}
                  label="Import key"
                  size="small"
                  color="primary"
                  variant="outlined"
                  disabled={importMutation.isPending}
                  onClick={() => setPgpPrompt(att)}
                />
              ))}
          </Box>
        ))}
      </Stack>

      <Dialog open={Boolean(pgpPrompt)} onClose={() => {
        if (pgpPrompt) {
          setPgpDismissed((prev) => new Set(prev).add(`${pgpPrompt.id}-${pgpPrompt.filename}`));
        }
        setPgpPrompt(null);
      }}>
        <DialogTitle>Import PGP public key?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This message includes what looks like a PGP public key
            {pgpPrompt ? ` (${pgpPrompt.filename})` : ''}. Add it to your public key list?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (pgpPrompt) {
                setPgpDismissed((prev) =>
                  new Set(prev).add(`${pgpPrompt.id}-${pgpPrompt.filename}`)
                );
              }
              setPgpPrompt(null);
            }}
          >
            Not now
          </Button>
          <Button
            variant="contained"
            disabled={!pgpPrompt || importMutation.isPending}
            onClick={() => pgpPrompt && importMutation.mutate(pgpPrompt)}
          >
            Import key
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
